import React from 'react';
import { Chip, ChipProps, alpha, useTheme } from '@mui/material';
import {
  CheckCircleOutline,
  HourglassEmpty,
  SwapHoriz,
  ExitToApp,
  School,
  Replay,
  TrendingUp,
  HelpOutline,
  RadioButtonUnchecked,
  CheckCircle,
  Cancel,
  ErrorOutline
} from '@mui/icons-material';
import {
  EnrollmentStatus,
  PromotionDecision,
  PromotionRunStatus,
  PromotionRunChunkStatus,
  ResultStatus,
  SessionStatus
} from '../../types/sessions';
import {
  AdmissionCycleStatus,
  ApplicationStatus,
  AdmissionEnquiryStatus,
} from '../../types/admissions';
import type { EmailStatus } from '../../types/email';

export type StatusKind =
  | { kind: 'session';          value: SessionStatus }
  | { kind: 'enrollment';       value: EnrollmentStatus }
  | { kind: 'result';           value: ResultStatus }
  | { kind: 'decision';         value: PromotionDecision }
  | { kind: 'runStatus';        value: PromotionRunStatus }
  | { kind: 'chunkStatus';      value: PromotionRunChunkStatus }
  | { kind: 'admissionCycle';   value: AdmissionCycleStatus }
  | { kind: 'application';      value: ApplicationStatus }
  | { kind: 'enquiry';          value: AdmissionEnquiryStatus }
  | { kind: 'email';            value: EmailStatus };

interface StatusChipProps extends Omit<ChipProps, 'label' | 'color' | 'icon'> {
  status: StatusKind;
  showIcon?: boolean;
}

// All status presentations live here so pages never copy-paste color choices.
// Palette is intentionally calm — these chips appear in lists where loudness
// hurts scanability.
const STATUS_REGISTRY: Record<
  string,
  { label: string; tone: ChipTone; Icon?: React.ElementType }
> = {
  // Session statuses
  'session:planning':  { label: 'Planning',  tone: 'pending',   Icon: HourglassEmpty },
  'session:active':    { label: 'Active',    tone: 'success',   Icon: CheckCircle },
  'session:completed': { label: 'Completed', tone: 'info',      Icon: CheckCircleOutline },
  'session:archived':  { label: 'Archived',  tone: 'neutral',   Icon: RadioButtonUnchecked },

  // Enrollment statuses
  'enrollment:enrolled':                { label: 'Enrolled',            tone: 'success',   Icon: CheckCircle },
  'enrollment:transferred_internally':  { label: 'Moved within session', tone: 'highlight', Icon: SwapHoriz },
  'enrollment:transferred_out':         { label: 'Transferred out',     tone: 'warning',   Icon: ExitToApp },
  'enrollment:dropped_out':             { label: 'Dropped out',         tone: 'warning',   Icon: ExitToApp },
  'enrollment:graduated':               { label: 'Graduated',           tone: 'info',      Icon: School },
  'enrollment:promoted':                { label: 'Promoted',            tone: 'success',   Icon: TrendingUp },
  'enrollment:retained':                { label: 'Retained',            tone: 'highlight', Icon: Replay },
  'enrollment:inconclusive':            { label: 'Inconclusive',        tone: 'neutral',   Icon: HelpOutline },

  // Result statuses
  'result:pending':         { label: 'Result pending',   tone: 'pending',  Icon: HourglassEmpty },
  'result:pass':            { label: 'Pass',             tone: 'success',  Icon: CheckCircle },
  'result:fail':            { label: 'Fail',             tone: 'error',    Icon: Cancel },
  'result:compartment':     { label: 'Compartment',      tone: 'warning',  Icon: ErrorOutline },
  'result:absent':          { label: 'Absent',           tone: 'neutral',  Icon: RadioButtonUnchecked },
  'result:not_applicable':  { label: 'N/A',              tone: 'neutral' },

  // Promotion decisions
  'decision:pending':           { label: 'Decide…',         tone: 'pending',   Icon: HourglassEmpty },
  'decision:promote':           { label: 'Promote',         tone: 'success',   Icon: TrendingUp },
  'decision:retain':            { label: 'Retain',          tone: 'highlight', Icon: Replay },
  'decision:graduate':          { label: 'Graduate',        tone: 'info',      Icon: School },
  'decision:drop':              { label: 'Drop',            tone: 'warning',   Icon: ExitToApp },
  'decision:manual_override':   { label: 'Manual',          tone: 'highlight', Icon: SwapHoriz },

  // Promotion run statuses
  'runStatus:draft':                { label: 'Draft',              tone: 'pending',  Icon: HourglassEmpty },
  'runStatus:in_progress':          { label: 'In progress',        tone: 'highlight', Icon: TrendingUp },
  'runStatus:partially_committed':  { label: 'Partially committed', tone: 'warning',  Icon: ErrorOutline },
  'runStatus:committed':            { label: 'Committed',          tone: 'success',  Icon: CheckCircle },
  'runStatus:cancelled':            { label: 'Cancelled',          tone: 'neutral',  Icon: RadioButtonUnchecked },

  // Chunk statuses
  'chunkStatus:pending':      { label: 'Pending',     tone: 'pending',   Icon: HourglassEmpty },
  'chunkStatus:in_progress':  { label: 'Committing',  tone: 'highlight', Icon: TrendingUp },
  'chunkStatus:committed':    { label: 'Committed',   tone: 'success',   Icon: CheckCircle },
  'chunkStatus:failed':       { label: 'Failed',      tone: 'error',     Icon: ErrorOutline },

  // Admission cycle statuses — scaled per the Draft → Open → Closed → Archived lifecycle.
  'admissionCycle:Draft':    { label: 'Draft',    tone: 'pending',   Icon: HourglassEmpty },
  'admissionCycle:Open':     { label: 'Open',     tone: 'success',   Icon: CheckCircle },
  'admissionCycle:Closed':   { label: 'Closed',   tone: 'neutral',   Icon: Cancel },
  'admissionCycle:Archived': { label: 'Archived', tone: 'neutral',   Icon: RadioButtonUnchecked },

  // Admission application statuses — 21-state funnel. Tones chosen so a
  // scanning officer can read a mixed list without eye strain.
  'application:Draft':                 { label: 'Draft',               tone: 'pending',   Icon: HourglassEmpty },
  'application:Submitted':             { label: 'Submitted',           tone: 'info',      Icon: CheckCircleOutline },
  'application:AppFeePending':         { label: 'App fee pending',     tone: 'warning',   Icon: HourglassEmpty },
  'application:UnderReview':           { label: 'Under review',        tone: 'highlight', Icon: TrendingUp },
  'application:DocsRequired':          { label: 'Docs required',       tone: 'warning',   Icon: ErrorOutline },
  'application:Ineligible':            { label: 'Ineligible',          tone: 'error',     Icon: Cancel },
  'application:AssessmentScheduled':   { label: 'Assessment scheduled',tone: 'highlight', Icon: CheckCircleOutline },
  'application:Absent':                { label: 'Absent',              tone: 'warning',   Icon: ErrorOutline },
  'application:Assessed':              { label: 'Assessed',            tone: 'info',      Icon: CheckCircleOutline },
  'application:Offered':               { label: 'Offered',             tone: 'highlight', Icon: TrendingUp },
  'application:OfferAccepted':         { label: 'Offer accepted',      tone: 'success',   Icon: CheckCircle },
  'application:OfferDeclined':         { label: 'Offer declined',      tone: 'neutral',   Icon: Cancel },
  'application:OfferLapsed':           { label: 'Offer lapsed',        tone: 'neutral',   Icon: Cancel },
  'application:Waitlisted':            { label: 'Waitlisted',          tone: 'info',      Icon: HourglassEmpty },
  'application:NotAdmitted':           { label: 'Not admitted',        tone: 'neutral',   Icon: Cancel },
  'application:Rejected':              { label: 'Rejected',            tone: 'error',     Icon: Cancel },
  'application:ConfirmedConditional':  { label: 'Confirmed (conditional)', tone: 'highlight', Icon: CheckCircleOutline },
  'application:AdmFeePending':         { label: 'Adm. fee pending',    tone: 'warning',   Icon: HourglassEmpty },
  'application:Confirmed':             { label: 'Confirmed',           tone: 'success',   Icon: CheckCircle },
  'application:Enrolled':              { label: 'Enrolled',            tone: 'success',   Icon: School },
  'application:Withdrawn':             { label: 'Withdrawn',           tone: 'neutral',   Icon: ExitToApp },

  // Enquiry statuses.
  'enquiry:new':            { label: 'New',            tone: 'pending',   Icon: HourglassEmpty },
  'enquiry:called':         { label: 'Called',         tone: 'highlight', Icon: CheckCircleOutline },
  'enquiry:applied':        { label: 'Applied',        tone: 'success',   Icon: CheckCircle },
  'enquiry:not_interested': { label: 'Not interested', tone: 'neutral',   Icon: Cancel },

  // Emails
  'email:sending': { label: 'Sending', tone: 'pending', Icon: HourglassEmpty },
  'email:sent':    { label: 'Sent',    tone: 'success', Icon: CheckCircle },
  'email:failed':  { label: 'Failed',  tone: 'error',   Icon: ErrorOutline },
};

/** Human label for a status — the same text the chip shows. Use wherever a
 *  status appears outside a chip (filter dropdowns, exports). */
export const statusLabel = (status: StatusKind): string =>
  STATUS_REGISTRY[`${status.kind}:${status.value}`]?.label ?? String(status.value);

export type ChipTone = 'success' | 'info' | 'warning' | 'error' | 'neutral' | 'pending' | 'highlight';

interface ToneChipProps extends Omit<ChipProps, 'label' | 'color' | 'icon'> {
  label: React.ReactNode;
  tone: ChipTone;
  icon?: React.ElementType;
}

/**
 * The status-chip look for any semantic label that isn't a registry status
 * (e.g. "12 days left"). StatusChip renders through this, so every tone has
 * exactly one palette across the app.
 */
export const ToneChip: React.FC<ToneChipProps> = ({ label, tone, icon: IconCmp, sx, ...rest }) => {
  const theme = useTheme();
  // Text takes the tone's dark shade on light backgrounds and its light shade on dark ones.
  const ink = theme.palette.mode === 'dark' ? 'light' : 'dark';
  // Tone palette. Soft fills, full-saturation text — premium feel, no neon.
  const tonePalette: Record<ChipTone, { bg: string; fg: string; border: string }> = {
    success:   { bg: alpha(theme.palette.success.main, 0.12),  fg: theme.palette.success[ink],  border: alpha(theme.palette.success.main, 0.32) },
    info:      { bg: alpha(theme.palette.info.main, 0.12),     fg: theme.palette.info[ink],     border: alpha(theme.palette.info.main, 0.32) },
    warning:   { bg: alpha(theme.palette.warning.main, 0.14),  fg: theme.palette.warning[ink],  border: alpha(theme.palette.warning.main, 0.34) },
    error:     { bg: alpha(theme.palette.error.main, 0.12),    fg: theme.palette.error[ink],    border: alpha(theme.palette.error.main, 0.32) },
    pending:   { bg: alpha(theme.palette.text.secondary, 0.10), fg: theme.palette.text.secondary, border: alpha(theme.palette.text.secondary, 0.24) },
    neutral:   { bg: alpha(theme.palette.text.secondary, 0.08), fg: theme.palette.text.secondary, border: alpha(theme.palette.text.secondary, 0.18) },
    highlight: { bg: alpha(theme.palette.primary.main, 0.10),   fg: theme.palette.primary[ink],   border: alpha(theme.palette.primary.main, 0.28) }
  };
  const palette = tonePalette[tone];

  return (
    <Chip
      size="small"
      label={label}
      icon={IconCmp ? <IconCmp sx={{ fontSize: '0.9rem !important' }} /> : undefined}
      variant="outlined"
      sx={{
        bgcolor: palette.bg,
        color: palette.fg,
        borderColor: palette.border,
        fontWeight: 500,
        letterSpacing: 0.1,
        height: 22,
        fontSize: 11,
        '& .MuiChip-label': { px: 1 },
        '& .MuiChip-icon': { color: palette.fg, marginLeft: '6px' },
        ...sx
      }}
      {...rest}
    />
  );
};

// Text-only by default — the registry's `tone` already carries the semantic
// signal via color. Callers that explicitly need an icon can pass showIcon.
const StatusChipBase: React.FC<StatusChipProps> = ({ status, showIcon = false, ...rest }) => {
  const key = `${status.kind}:${status.value}`;
  const entry = STATUS_REGISTRY[key];
  if (!entry) return <Chip label={status.value} size="small" />;
  return (
    <ToneChip
      label={entry.label}
      tone={entry.tone}
      icon={showIcon ? entry.Icon : undefined}
      {...rest}
    />
  );
};

const StatusChip = React.memo(StatusChipBase);
export default StatusChip;
